import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import useModalState, { initialModalOptions, type ModalOptionsType } from '../hooks/useModalState';

const initialState = {
  modal: null,
  handleModal: (m?: any, p?: any): any => ({ m, p }),
  modalContent: null,
  modalOptions: initialModalOptions,
  isModalOpen: null,
};

const ModalContext = React.createContext(initialState);
const { Provider } = ModalContext;
const ModalActionsContext = React.createContext<
  | {
      closeModal: () => void;
      openModal: (content: React.ReactNode, options?: ModalOptionsType) => void;
    }
  | undefined
>(undefined);
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modal, handleModal, modalContent, modalOptions] = useModalState();
  const isModalOpen = modal;
  const currentModal = useRef({ modal, handleModal });
  useIsomorphicLayoutEffect(() => {
    currentModal.current = { modal, handleModal };
  }, [modal, handleModal]);

  const closeModal = useCallback(() => {
    if (currentModal.current.modal) currentModal.current.handleModal(null);
  }, []);
  const openModal = useCallback((content: React.ReactNode, options: ModalOptionsType = {}) => {
    currentModal.current.handleModal(content, { ...initialModalOptions, ...options });
  }, []);
  const actions = useMemo(() => ({ closeModal, openModal }), [closeModal, openModal]);

  return (
    <ModalActionsContext.Provider value={actions}>
      <Provider value={{ modal, handleModal, modalContent, modalOptions, isModalOpen }}>
        {children}
      </Provider>
    </ModalActionsContext.Provider>
  );
};

// Action-only consumers must not rerender whenever a modal opens or changes.
const useModalActions = () => {
  const actions = useContext(ModalActionsContext);
  if (!actions) throw new Error('Modal actions must be used within a ModalProvider');
  return actions;
};

const useCloseModal = () => useModalActions().closeModal;
const useOpenModal = () => useModalActions().openModal;

const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within a ModalProvider');
  }

  // Not sure why we were exporting each control functions twice? But might as well reference the same code.
  const hideModalFunc = () => context.handleModal(null);
  const hideModal = hideModalFunc;
  const showModalFunc = (m, t: ModalOptionsType = {}) =>
    context.handleModal(m, { ...initialState.modalOptions, ...t });
  const showModal = showModalFunc;

  const isModalOpen = !!context.modal;
  return { ...context, hideModalFunc, hideModal, showModalFunc, showModal, isModalOpen };
};

export { ModalContext, ModalProvider, initialState, useModal, useCloseModal, useOpenModal };
